'use client';

import { c as _c } from "react/compiler-runtime";
import { Button, type ButtonProps } from '@heroui/react';
import { cn } from '@heroui/theme';
import useEmblaCarousel, { type UseEmblaCarouselType } from 'embla-carousel-react';
import { type HTMLAttributes, type KeyboardEvent, forwardRef, useCallback, useEffect, useState } from 'react';
import { twMerge } from 'tailwind-merge';
import { CarouselContext } from '@/contexts/carousel-context';
import useCarousel from '@/hooks/use-carousel';
type UseCarouselParameters = Parameters<typeof useEmblaCarousel>;
type CarouselApi = UseEmblaCarouselType[1];
type CarouselOptions = UseCarouselParameters[0];
type CarouselPlugin = UseCarouselParameters[1];
export type CarouselProps = {
  opts?: CarouselOptions;
  plugins?: CarouselPlugin;
  orientation?: 'horizontal' | 'vertical';
  setApi?: (api: CarouselApi) => void;
  disableKeydown?: boolean;
  setActiveIndex?: (index: number) => void;
};
const Carousel = forwardRef<HTMLDivElement, HTMLAttributes<HTMLDivElement> & CarouselProps>((t0, ref) => {
  const $ = _c(56);
  let children;
  let className;
  let opts;
  let plugins;
  let props;
  let setActiveIndex;
  let setApi;
  let t1;
  let t2;
  if ($[0] !== t0) {
    ({
      orientation: t1,
      opts,
      setApi,
      plugins,
      setActiveIndex,
      disableKeydown: t2,
      className,
      children,
      ...props
    } = t0);
    $[0] = t0;
    $[1] = children;
    $[2] = className;
    $[3] = opts;
    $[4] = plugins;
    $[5] = props;
    $[6] = setActiveIndex;
    $[7] = setApi;
    $[8] = t1;
    $[9] = t2;
  } else {
    children = $[1];
    className = $[2];
    opts = $[3];
    plugins = $[4];
    props = $[5];
    setActiveIndex = $[6];
    setApi = $[7];
    t1 = $[8];
    t2 = $[9];
  }
  const orientation = t1 === undefined ? "horizontal" : t1;
  const disableKeydown = t2 === undefined ? false : t2;
  const t3 = orientation === "horizontal" ? "x" : "y";
  let t4;
  if ($[10] !== opts || $[11] !== t3) {
    t4 = {
      ...opts,
      axis: t3
    };
    $[10] = opts;
    $[11] = t3;
    $[12] = t4;
  } else {
    t4 = $[12];
  }
  const [carouselRef, api] = useEmblaCarousel(t4, plugins);
  const [canScrollPrev, setCanScrollPrev] = useState(false);
  const [canScrollNext, setCanScrollNext] = useState(false);
  let t5;
  if ($[13] === Symbol.for("react.memo_cache_sentinel")) {
    t5 = api_0 => {
      if (!api_0) {
        return;
      }
      setCanScrollPrev(api_0.canScrollPrev());
      setCanScrollNext(api_0.canScrollNext());
    };
    $[13] = t5;
  } else {
    t5 = $[13];
  }
  const onSelect = t5;
  let t6;
  if ($[14] !== api) {
    t6 = () => {
      api?.scrollPrev();
    };
    $[14] = api;
    $[15] = t6;
  } else {
    t6 = $[15];
  }
  const scrollPrev = t6;
  let t7;
  if ($[16] !== api) {
    t7 = () => {
      api?.scrollNext();
    };
    $[16] = api;
    $[17] = t7;
  } else {
    t7 = $[17];
  }
  const scrollNext = t7;
  let t8;
  if ($[18] !== scrollNext || $[19] !== scrollPrev) {
    t8 = event => {
      if (event.key === "ArrowLeft") {
        event.preventDefault();
        scrollPrev();
      } else {
        if (event.key === "ArrowRight") {
          event.preventDefault();
          scrollNext();
        }
      }
    };
    $[18] = scrollNext;
    $[19] = scrollPrev;
    $[20] = t8;
  } else {
    t8 = $[20];
  }
  const handleKeyDown = t8;
  let t10;
  let t9;
  if ($[21] !== api || $[22] !== setApi) {
    t9 = () => {
      if (!api || !setApi) {
        return;
      }
      setApi(api);
    };
    t10 = [api, setApi];
    $[21] = api;
    $[22] = setApi;
    $[23] = t10;
    $[24] = t9;
  } else {
    t10 = $[23];
    t9 = $[24];
  }
  useEffect(t9, t10);
  let t11;
  let t12;
  if ($[25] !== api) {
    t11 = () => {
      if (!api) {
        return;
      }
      onSelect(api);
      api.on("reInit", onSelect);
      api.on("select", onSelect);
      return () => {
        api?.off("select", onSelect);
      };
    };
    t12 = [api, onSelect];
    $[25] = api;
    $[26] = t11;
    $[27] = t12;
  } else {
    t11 = $[26];
    t12 = $[27];
  }
  useEffect(t11, t12);
  let t13;
  let t14;
  if ($[28] !== api || $[29] !== setActiveIndex) {
    t13 = () => {
      if (!api || !setActiveIndex) {
        return;
      }
      const onSelect_0 = () => {
        const index = api.selectedScrollSnap();
        setActiveIndex(index);
      };
      onSelect_0();
      api.on("select", onSelect_0);
      return () => {
        api.off("select", onSelect_0);
      };
    };
    t14 = [api, setActiveIndex];
    $[28] = api;
    $[29] = setActiveIndex;
    $[30] = t13;
    $[31] = t14;
  } else {
    t13 = $[30];
    t14 = $[31];
  }
  useEffect(t13, t14);
  const t15 = orientation || (opts?.axis === "y" ? "vertical" : "horizontal");
  let t16;
  if ($[32] !== api || $[33] !== canScrollNext || $[34] !== canScrollPrev || $[35] !== carouselRef || $[36] !== opts || $[37] !== scrollNext || $[38] !== scrollPrev || $[39] !== setActiveIndex || $[40] !== t15) {
    t16 = {
      carouselRef,
      api,
      opts,
      orientation: t15,
      scrollPrev,
      scrollNext,
      canScrollPrev,
      canScrollNext,
      setActiveIndex
    };
    $[32] = api;
    $[33] = canScrollNext;
    $[34] = canScrollPrev;
    $[35] = carouselRef;
    $[36] = opts;
    $[37] = scrollNext;
    $[38] = scrollPrev;
    $[39] = setActiveIndex;
    $[40] = t15;
    $[41] = t16;
  } else {
    t16 = $[41];
  }
  let t17;
  if ($[42] !== disableKeydown || $[43] !== handleKeyDown) {
    t17 = disableKeydown ? _temp : handleKeyDown;
    $[42] = disableKeydown;
    $[43] = handleKeyDown;
    $[44] = t17;
  } else {
    t17 = $[44];
  }
  let t18;
  if ($[45] !== className) {
    t18 = cn("relative", className);
    $[45] = className;
    $[46] = t18;
  } else {
    t18 = $[46];
  }
  let t19;
  if ($[47] !== children || $[48] !== props || $[49] !== ref || $[50] !== t17 || $[51] !== t18) {
    t19 = <div ref={ref} onKeyDownCapture={t17} className={t18} role="region" aria-roledescription="carousel" {...props}>{children}</div>;
    $[47] = children;
    $[48] = props;
    $[49] = ref;
    $[50] = t17;
    $[51] = t18;
    $[52] = t19;
  } else {
    t19 = $[52];
  }
  let t20;
  if ($[53] !== t16 || $[54] !== t19) {
    t20 = <CarouselContext.Provider value={t16}>{t19}</CarouselContext.Provider>;
    $[53] = t16;
    $[54] = t19;
    $[55] = t20;
  } else {
    t20 = $[55];
  }
  return t20;
});
Carousel.displayName = 'Carousel';
const CarouselContent = forwardRef<HTMLDivElement, HTMLAttributes<HTMLDivElement>>((t0, ref) => {
  const $ = _c(13);
  let className;
  let props;
  if ($[0] !== t0) {
    ({
      className,
      ...props
    } = t0);
    $[0] = t0;
    $[1] = className;
    $[2] = props;
  } else {
    className = $[1];
    props = $[2];
  }
  const {
    carouselRef,
    orientation
  } = useCarousel();
  const t1 = orientation === "horizontal" ? "-ml-4" : "-mt-4 flex-col";
  let t2;
  if ($[3] !== className || $[4] !== t1) {
    t2 = cn("flex", t1, className);
    $[3] = className;
    $[4] = t1;
    $[5] = t2;
  } else {
    t2 = $[5];
  }
  let t3;
  if ($[6] !== props || $[7] !== ref || $[8] !== t2) {
    t3 = <div ref={ref} className={t2} {...props} />;
    $[6] = props;
    $[7] = ref;
    $[8] = t2;
    $[9] = t3;
  } else {
    t3 = $[9];
  }
  let t4;
  if ($[10] !== carouselRef || $[11] !== t3) {
    t4 = <div ref={carouselRef} className="overflow-hidden ">{t3}</div>;
    $[10] = carouselRef;
    $[11] = t3;
    $[12] = t4;
  } else {
    t4 = $[12];
  }
  return t4;
});
CarouselContent.displayName = 'CarouselContent';
const CarouselItem = forwardRef<HTMLDivElement, HTMLAttributes<HTMLDivElement>>((t0, ref) => {
  const $ = _c(10);
  let className;
  let props;
  if ($[0] !== t0) {
    ({
      className,
      ...props
    } = t0);
    $[0] = t0;
    $[1] = className;
    $[2] = props;
  } else {
    className = $[1];
    props = $[2];
  }
  const {
    orientation
  } = useCarousel();
  const t1 = orientation === "horizontal" ? "pl-4" : "pt-4";
  let t2;
  if ($[3] !== className || $[4] !== t1) {
    t2 = cn("min-w-0 shrink-0 grow-0 basis-full", t1, className);
    $[3] = className;
    $[4] = t1;
    $[5] = t2;
  } else {
    t2 = $[5];
  }
  let t3;
  if ($[6] !== props || $[7] !== ref || $[8] !== t2) {
    t3 = <div ref={ref} role="group" aria-roledescription="slide" className={t2} {...props} />;
    $[6] = props;
    $[7] = ref;
    $[8] = t2;
    $[9] = t3;
  } else {
    t3 = $[9];
  }
  return t3;
});
CarouselItem.displayName = 'CarouselItem';
interface CarouselPreviousButtonProps extends ButtonProps {
  disableDefaultOrientationProps?: boolean;
}
const CarouselPrevious = t0 => {
  const $ = _c(22);
  let children;
  let className;
  let isIconOnly;
  let props;
  let ref;
  let t1;
  let t2;
  let t3;
  if ($[0] !== t0) {
    ({
      disableDefaultOrientationProps: t1,
      ref,
      className,
      children,
      variant: t2,
      size: t3,
      isIconOnly,
      ...props
    } = t0);
    $[0] = t0;
    $[1] = children;
    $[2] = className;
    $[3] = isIconOnly;
    $[4] = props;
    $[5] = ref;
    $[6] = t1;
    $[7] = t2;
    $[8] = t3;
  } else {
    children = $[1];
    className = $[2];
    isIconOnly = $[3];
    props = $[4];
    ref = $[5];
    t1 = $[6];
    t2 = $[7];
    t3 = $[8];
  }
  const disableDefaultOrientationProps = t1 === undefined ? false : t1;
  const variant = t2 === undefined ? "flat" : t2;
  const size = t3 === undefined ? "sm" : t3;
  const {
    orientation,
    scrollPrev,
    canScrollPrev
  } = useCarousel();
  const t4 = !disableDefaultOrientationProps && (orientation === "horizontal" ? "-left-12 top-1/2 -translate-y-1/2" : "-top-12 left-1/2 -translate-x-1/2 rotate-90");
  let t5;
  if ($[9] !== className || $[10] !== t4) {
    t5 = twMerge(t4, className);
    $[9] = className;
    $[10] = t4;
    $[11] = t5;
  } else {
    t5 = $[11];
  }
  const t6 = !canScrollPrev;
  let t7;
  if ($[12] !== children || $[13] !== isIconOnly || $[14] !== props || $[15] !== ref || $[16] !== scrollPrev || $[17] !== size || $[18] !== t5 || $[19] !== t6 || $[20] !== variant) {
    t7 = <Button ref={ref} variant={variant} size={size} className={t5} isIconOnly={isIconOnly} isDisabled={t6} onPress={scrollPrev} {...props}>{children}</Button>;
    $[12] = children;
    $[13] = isIconOnly;
    $[14] = props;
    $[15] = ref;
    $[16] = scrollPrev;
    $[17] = size;
    $[18] = t5;
    $[19] = t6;
    $[20] = variant;
    $[21] = t7;
  } else {
    t7 = $[21];
  }
  return t7;
};
CarouselPrevious.displayName = 'CarouselPrevious';
interface CarouselNextButtonProps extends ButtonProps {
  disableDefaultOrientationProps?: boolean;
}
const CarouselNext = t0 => {
  const $ = _c(22);
  let children;
  let className;
  let isIconOnly;
  let props;
  let ref;
  let t1;
  let t2;
  let t3;
  if ($[0] !== t0) {
    ({
      disableDefaultOrientationProps: t1,
      ref,
      className,
      children,
      variant: t2,
      size: t3,
      isIconOnly,
      ...props
    } = t0);
    $[0] = t0;
    $[1] = children;
    $[2] = className;
    $[3] = isIconOnly;
    $[4] = props;
    $[5] = ref;
    $[6] = t1;
    $[7] = t2;
    $[8] = t3;
  } else {
    children = $[1];
    className = $[2];
    isIconOnly = $[3];
    props = $[4];
    ref = $[5];
    t1 = $[6];
    t2 = $[7];
    t3 = $[8];
  }
  const disableDefaultOrientationProps = t1 === undefined ? false : t1;
  const variant = t2 === undefined ? "flat" : t2;
  const size = t3 === undefined ? "sm" : t3;
  const {
    orientation,
    scrollNext,
    canScrollNext
  } = useCarousel();
  const t4 = !disableDefaultOrientationProps && (orientation === "horizontal" ? "-right-12 top-1/2 -translate-y-1/2" : "-bottom-12 left-1/2 -translate-x-1/2 rotate-90");
  let t5;
  if ($[9] !== className || $[10] !== t4) {
    t5 = twMerge(t4, className);
    $[9] = className;
    $[10] = t4;
    $[11] = t5;
  } else {
    t5 = $[11];
  }
  const t6 = !canScrollNext;
  let t7;
  if ($[12] !== children || $[13] !== isIconOnly || $[14] !== props || $[15] !== ref || $[16] !== scrollNext || $[17] !== size || $[18] !== t5 || $[19] !== t6 || $[20] !== variant) {
    t7 = <Button ref={ref} variant={variant} size={size} className={t5} isIconOnly={isIconOnly} isDisabled={t6} onPress={scrollNext} {...props}>{children}</Button>;
    $[12] = children;
    $[13] = isIconOnly;
    $[14] = props;
    $[15] = ref;
    $[16] = scrollNext;
    $[17] = size;
    $[18] = t5;
    $[19] = t6;
    $[20] = variant;
    $[21] = t7;
  } else {
    t7 = $[21];
  }
  return t7;
};
CarouselNext.displayName = 'CarouselNext';
export { Carousel, CarouselContent, CarouselItem, CarouselNext, CarouselPrevious, type CarouselApi };
function _temp() {
  return null;
}